import {
    Profiles,
    CompanyDetails,
    BusinessAddresses,
    CompanyDirectors,
    Verifyid,
    PastInvoices,
    DebtorInvoice,
    BusinessFinancials
} from './requests';

/**
 * Onboarding Service
 * Handles the completion of the onboarding flow by mapping collected data to backend APIs
 */
class OnboardingService {
    /**
     * Complete the entire onboarding process
     * @param {Object} onboardingData - All collected onboarding data from store
     * @param {number|null} profileId - Existing profile ID (if any)
     * @returns {Object} - Results of all API calls
     */
    async completeOnboarding(onboardingData, profileId) {
        const results = {
            profile: null,
            companyDetails: null,
            businessFinancials: null,
            address: null,
            directors: [],
            verifyId: null,
            pastInvoices: null
        };

        try {
            // Step 1: Create/Update Profile
            results.profile = await this.saveProfile(onboardingData, profileId);

            // Step 2: Save Company Details
            results.companyDetails = await this.saveCompanyDetails(
                onboardingData.businessData,
                results.profile.id
            );

            // Step 3: Save Business Financials (required for Business Profile page)
            results.businessFinancials = await this.saveBusinessFinancials(
                results.profile.id
            );

            // Step 4: Save Business Address (from CIPC)
            if (onboardingData.businessData.businesses && onboardingData.businessData.businesses.length > 0) {
                results.address = await this.saveBusinessAddress(
                    onboardingData.businessData,
                    results.companyDetails.id
                );
            }

            // Step 5: Save Directors (from CIPC, active only)
            if (onboardingData.businessData.businesses && onboardingData.businessData.businesses.length > 0) {
                results.directors = await this.saveDirectors(
                    onboardingData.businessData,
                    results.companyDetails.id
                );
            }

            // Step 6: Save VerifyID data
            results.verifyId = await this.saveVerifyId(
                onboardingData,
                results.profile.id
            );

            // Step 7: Save Past Invoices (if uploaded)
            if (onboardingData.pastInvoices.uploadMethod === 'upload' &&
                onboardingData.pastInvoices.files &&
                onboardingData.pastInvoices.files.length > 0) {
                results.pastInvoices = await this.savePastInvoices(
                    onboardingData.pastInvoices,
                    results.profile.id
                );
            }

            return {
                success: true,
                profileId: results.profile.id,
                results
            };
        } catch (error) {
            console.error('Onboarding completion error:', error);
            throw error;
        }
    }

    /**
     * Save or update profile
     */
    async saveProfile(onboardingData, existingProfileId) {
        const businessName = onboardingData.businessData.selectedBusiness?.name;
        const cipcRegNo = onboardingData.businessData.selectedBusiness?.registrationNumber;

        if (existingProfileId) {
            // For updates, fetch existing profile first to check if cipcRegNo is changing
            const existingProfile = await Profiles.get(existingProfileId);

            const profileData = {
                businessName
            };

            // Only include cipcRegNo if it's different from the existing value
            if (existingProfile.data.cipcRegNo !== cipcRegNo) {
                profileData.cipcRegNo = cipcRegNo;
            }

            const response = await Profiles.edit(existingProfileId, profileData);
            return response.data;
        } else {
            // For new profiles, include both fields
            const profileData = {
                businessName,
                cipcRegNo
            };
            const response = await Profiles.add(profileData);
            return response.data;
        }
    }

    /**
     * Save company details (create or update existing)
     */
    async saveCompanyDetails(businessData, profileId) {
        const selectedBusiness = businessData.selectedBusiness;

        const companyData = {
            name: selectedBusiness?.name,
            registrationNumber: selectedBusiness?.registrationNumber,
            type: selectedBusiness?.type,
            status: selectedBusiness?.status,
            registrationDate: selectedBusiness?.registrationDate,
            profile: profileId,
            verified: false
        };

        // Check if company details already exists for this profile
        try {
            // Use getAll with query parameter to find by profile
            const existingCompanyResponse = await CompanyDetails.getAll();
            const existingCompany = existingCompanyResponse.data?.find(cd => cd.profile === profileId);

            if (existingCompany) {
                // Update existing company details
                const response = await CompanyDetails.edit(existingCompany.id, companyData);
                return response.data;
            }
        } catch (error) {
            // No existing company details found, will create new one
            console.log('No existing company details found, creating new one');
        }

        // Create new company details
        const response = await CompanyDetails.add(companyData);
        return response.data;
    }

    /**
     * Save business financials (create new or link existing to profile)
     * This is required for the Business Profile page to function correctly
     */
    async saveBusinessFinancials(profileId) {
        const businessFinancialsData = {
            verified: false
        };

        try {
            // Create new business financials
            const response = await BusinessFinancials.add(businessFinancialsData);
            const businessFinancialsObj = response.data;

            if (businessFinancialsObj && businessFinancialsObj.id) {
                // Link business financials to profile
                await Profiles.edit(profileId, {
                    businessFinancials: businessFinancialsObj.id
                });
            }

            return businessFinancialsObj;
        } catch (error) {
            console.error('Error creating business financials:', error);
            throw error;
        }
    }

    /**
     * Save business address from CIPC data
     */
    async saveBusinessAddress(businessData, companyDetailsId) {
        // Parse CIPC address from businesses array (contains full CIPC response)
        const cipcData = businessData.businesses?.[0]; // First business should have full CIPC data
        const physicalAddress = cipcData?.Companyinformation?.PhysicalAddress;

        if (!physicalAddress) {
            console.warn('No physical address found in CIPC data');
            return null;
        }

        const addressData = this.parsePhysicalAddress(physicalAddress);
        addressData.companyDetails = companyDetailsId;

        const response = await BusinessAddresses.add(addressData);
        return response.data;
    }

    /**
     * Parse CIPC physical address string into components
     * Example: "Unit 5, 123 Main Street, Suburb, City, Province, 2001"
     */
    parsePhysicalAddress(addressString) {
        const parts = addressString.split(',').map(s => s.trim());
        const length = parts.length;

        // Typical format: UnitNo, StreetName, Suburb, City, Province, PostalCode
        return {
            unitNo: parts[0] || '',
            streetName: length > 5 ? parts[1] : parts[0] || '',
            suburb: length > 5 ? parts[2] : parts[1] || '',
            city: length > 5 ? parts[3] : parts[2] || '',
            province: length > 5 ? parts[4] : parts[3] || '',
            postalCode: length > 5 ? parts[5] : parts[4] || ''
        };
    }

    /**
     * Save directors from CIPC data (active directors only, avoid duplicates)
     */
    async saveDirectors(businessData, companyDetailsId) {
        const cipcData = businessData.businesses?.[0];
        const directorInfo = cipcData?.Directorinformation;

        if (!directorInfo) {
            console.warn('No director information found in CIPC data');
            return [];
        }

        const directors = [];

        // Get existing directors for this company
        let existingDirectors = [];
        try {
            const existingResponse = await CompanyDirectors.get({ companyDetails: companyDetailsId });
            existingDirectors = existingResponse.data || [];
        } catch (error) {
            console.log('No existing directors found');
        }

        for (let key in directorInfo) {
            const director = directorInfo[key];

            // Only save active directors
            if (director.DirectorStatusCode === 'Active') {
                const directorData = {
                    firstName: director.FirstName || '',
                    lastName: director.LastName || '',
                    idNo: director.IDNo || '',
                    cellNo: director.CellNo || '',
                    email: '', // Not provided by CIPC
                    companyDetails: companyDetailsId,
                    verified: false
                };

                try {
                    // Check if director already exists (by ID number)
                    const existingDirector = existingDirectors.find(
                        d => d.idNo === directorData.idNo
                    );

                    if (existingDirector) {
                        // Update existing director
                        const response = await CompanyDirectors.edit(existingDirector.id, directorData);
                        directors.push(response.data);
                    } else {
                        // Create new director
                        const response = await CompanyDirectors.add(directorData);
                        directors.push(response.data);
                    }
                } catch (error) {
                    console.error(`Failed to save director ${director.FirstName} ${director.LastName}:`, error);
                    // Continue with other directors even if one fails
                }
            }
        }

        return directors;
    }

    /**
     * Save VerifyID data (combines identity verification and CIPC data)
     */
    async saveVerifyId(onboardingData, profileId) {
        const cipcData = onboardingData.businessData.businesses?.[0];

        const verifyIdData = {
            idNumber: onboardingData.identityData.idNumber,
            verified: onboardingData.identityData.verified,
            faceVerification: JSON.stringify(onboardingData.identityData.faceVerification || {}),
            directorInformation: JSON.stringify(cipcData?.Directorinformation || {}),
            companyInformation: JSON.stringify(cipcData?.Companyinformation || {}),
            profile: profileId
        };

        const response = await Verifyid.add(verifyIdData);
        return response.data;
    }

    /**
     * Save past invoices and debtor invoice entries
     */
    async savePastInvoices(pastInvoicesData, profileId) {
        // Create pastinvoices container
        const containerData = {
            profile: profileId,
            verified: false
        };

        const containerResponse = await PastInvoices.add(containerData);
        const containerId = containerResponse.data.id;

        // Create debtorinvoice entries for each uploaded file
        const invoices = [];

        for (let file of pastInvoicesData.files) {
            const invoiceData = {
                // Note: Step 4 currently only uploads files, doesn't collect metadata
                // These fields would be empty for now
                companyName: '',
                companyEmail: '',
                invoiceAmount: 0,
                invoiceCopy: [file.id], // File ID from upload
                pastInvoices: containerId,
                verified: false
            };

            try {
                const response = await DebtorInvoice.add(invoiceData);
                invoices.push(response.data);
            } catch (error) {
                console.error(`Failed to save invoice ${file.name}:`, error);
                // Continue with other invoices even if one fails
            }
        }

        return {
            container: containerResponse.data,
            invoices
        };
    }
}

export default new OnboardingService();
